import React, { useEffect } from 'react';
import { Col, InputNumber, Row, Select } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';

import { ClientFeatureConfig } from '../../../types/Feature';
import { QuoteDenyingConfigAttributes, VehicleLimitations } from '../../../../../server/src/models';
import FormListAddButton from '../../FormListAddButton';
import FormListRemoveButton from '../../FormListRemoveButton';

const { Option } = Select;
const cardHeaderTitleStyle = { fontSize: '18px' };

interface TransportTypeLimitationsProps {
    itemKey: number;
    itemName: number;
    config: VehicleLimitations | undefined;
    isDetailMode: boolean;
    remove: (val: number | number[]) => void;
}

const TransportTypeLimitations: React.FC<TransportTypeLimitationsProps> = ({
    itemKey,
    itemName,
    config,
    isDetailMode,
    remove,
}: TransportTypeLimitationsProps) => {
    const optList = [
        'Walk',
        'Bike',
        'Cargo Bike',
        'Steerwagon',
        'Motorbike',
        'Cargo Bike XL',
        'Motorbike XL',
        'Car',
        'Van',
        'Truck',
    ];
    const options = optList.map((opt) => ({ value: opt.toLowerCase().replace(/ /gi, ''), label: opt }));

    return (
        <div
            style={{
                marginTop: '10px',
                paddingTop: '10px',
                borderTop: 'dashed 2px #aaa',
            }}
            key={itemKey}
        >
            <Row gutter={24}>
                <Col span={6}>
                    <Form.Item name={[itemKey, 'transportType']} label="Transport type" labelCol={{ span: 12 }}>
                        {isDetailMode ? (
                            config?.transportType
                        ) : (
                            <Select>
                                {options.map(({ label: optLab, value: val }) => (
                                    <Option key={val} value={val}>
                                        {optLab}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
                <Col span={6} offset={1}>
                    <Form.Item name={[itemKey, 'maxDistance']} label="Max distance (km)" labelCol={{ span: 12 }}>
                        {isDetailMode ? config?.maxDistance : <InputNumber />}
                    </Form.Item>
                </Col>
                {!isDetailMode && (
                    <Col span={1} offset={8}>
                        <FormListRemoveButton name={itemName} isDetailMode={isDetailMode} remove={remove} />
                    </Col>
                )}
            </Row>
            <Row gutter={24}>
                <Col span={6}>
                    <Form.Item name={[itemKey, 'maxWeight']} label="Max weight (kg)" labelCol={{ span: 12 }}>
                        {isDetailMode ? config?.maxWeight : <InputNumber />}
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name={[itemKey, 'maxHeight']} label="Height (cm)" labelCol={{ span: 12 }}>
                        {isDetailMode ? config?.maxHeight : <InputNumber />}
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name={[itemKey, 'maxWidth']} label="Width (cm)" labelCol={{ span: 12 }}>
                        {isDetailMode ? config?.maxWidth : <InputNumber />}
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name={[itemKey, 'maxLength']} label="Length (cm)" labelCol={{ span: 12 }}>
                        {isDetailMode ? config?.maxLength : <InputNumber />}
                    </Form.Item>
                </Col>
            </Row>
        </div>
    );
};

interface TransportTypeLimitationsSectionProps {
    isDetailMode: boolean;
    formInstance: FormInstance;
    transportLimitationsConfig?: ClientFeatureConfig<QuoteDenyingConfigAttributes>;
}

const TransportTypeLimitationsSection: React.FC<TransportTypeLimitationsSectionProps> = (
    props: TransportTypeLimitationsSectionProps,
) => {
    const { transportLimitationsConfig: config, formInstance, isDetailMode } = props;

    useEffect(() => {
        if (config) {
            formInstance.setFields([
                {
                    name: 'vehicleLimitations',
                    value: config.config?.vehicleLimitations,
                },
            ]);
        }
    }, [config]);

    return (
        <div className="card border-0" id="vehicle-limitations-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Vehicles configuration
                </p>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.List name="vehicleLimitations">
                            {(fees, { add: newLimitation, remove: removeLimitation }): JSX.Element => (
                                <>
                                    {fees.map((fee) => (
                                        <TransportTypeLimitations
                                            key={fee.key}
                                            itemName={fee.name}
                                            itemKey={fee.key}
                                            remove={removeLimitation}
                                            isDetailMode={isDetailMode}
                                            config={config?.config?.vehicleLimitations?.[fee.key]}
                                        />
                                    ))}
                                    <FormListAddButton
                                        id="add-vehicle"
                                        add={newLimitation}
                                        isDetailMode={isDetailMode}
                                        text="Add a vehicle"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default TransportTypeLimitationsSection;
